import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, CheckCircle2, Loader2, MailWarning } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { ApiError, resendVerificationRequest, verifyEmailRequest } from '@/lib/api';
import { APP_VERSION } from '@/lib/version';

const resendSchema = z.object({
  email: z.string().trim().min(1, 'errors.validation.required').email('errors.validation.email'),
});

type ResendValues = z.infer<typeof resendSchema>;

type Status = 'loading' | 'success' | 'error' | 'missing';

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<Status>(token ? 'loading' : 'missing');
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    verifyEmailRequest(token)
      .then(() => setStatus('success'))
      .catch(() => setStatus('error'));
  }, [token]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResendValues>({
    resolver: zodResolver(resendSchema),
  });

  const onResend = async (data: ResendValues) => {
    try {
      await resendVerificationRequest(data.email.trim());
      toast.success(t('verifyEmail.resendSuccess'));
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        toast.error(t('verifyEmail.resendRateLimited'));
      } else {
        toast.error(t('verifyEmail.resendSuccess'));
      }
    }
  };

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-background p-4">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(120% 90% at 50% 0%, hsl(var(--accent) / 0.55) 0%, hsl(var(--background)) 62%)',
        }}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-[0.16em] left-1/2 -translate-x-1/2 select-none whitespace-nowrap font-serif italic leading-none text-foreground/[0.025]"
        style={{ fontSize: 'clamp(140px, 26vw, 420px)' }}
      >
        License
      </span>

      <div className="relative w-full max-w-[400px] motion-safe:duration-500 motion-safe:animate-in motion-safe:fade-in">
        <div className="rounded-[10px] border bg-card px-6 py-8 shadow-[0_1px_0_0_hsl(var(--foreground)/0.03),0_16px_48px_-16px_hsl(33_18%_4%/0.55)] sm:px-9 sm:py-10">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2.5">
              <span className="h-2.5 w-2.5 rounded-[3px] bg-primary" aria-hidden="true" />
              <span className="text-[19px] font-semibold tracking-tight text-foreground">
                License
              </span>
            </span>
          </div>

          {status === 'loading' && (
            <div className="mt-9 flex flex-col items-center gap-4 py-6 text-center" role="status">
              <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">{t('verifyEmail.verifying')}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="mt-9">
              <CheckCircle2 className="h-9 w-9 text-status-active" aria-hidden="true" />
              <h1 className="mt-4 text-[24px] font-semibold leading-tight tracking-tight text-foreground">
                {t('verifyEmail.successTitle')}
              </h1>
              <p className="mt-2 font-serif text-[16px] italic leading-snug text-muted-foreground">
                {t('verifyEmail.successSubtitle')}
              </p>
              <Button asChild className="mt-8 h-11 w-full">
                <Link to={isAuthenticated ? '/' : '/login'}>
                  {isAuthenticated ? t('verifyEmail.goHome') : t('verifyEmail.goToLogin')}
                </Link>
              </Button>
            </div>
          )}

          {(status === 'error' || status === 'missing') && (
            <div className="mt-9">
              <MailWarning className="h-9 w-9 text-status-pending" aria-hidden="true" />
              <h1 className="mt-4 text-[24px] font-semibold leading-tight tracking-tight text-foreground">
                {t('verifyEmail.errorTitle')}
              </h1>
              <p className="mt-2 font-serif text-[16px] italic leading-snug text-muted-foreground">
                {status === 'missing'
                  ? t('verifyEmail.missingSubtitle')
                  : t('verifyEmail.errorSubtitle')}
              </p>

              <form onSubmit={handleSubmit(onResend)} className="mt-8 space-y-5" noValidate>
                <div className="space-y-2">
                  <Label htmlFor="resend-email">{t('verifyEmail.resendLabel')}</Label>
                  <Input
                    id="resend-email"
                    type="email"
                    autoComplete="email"
                    placeholder={t('verifyEmail.resendPlaceholder')}
                    disabled={isSubmitting}
                    className="h-11"
                    {...register('email')}
                  />
                  {errors.email && (
                    <p className="flex items-center gap-1.5 text-sm text-destructive" role="alert">
                      <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
                      {t(String(errors.email.message))}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  variant="secondary"
                  className="h-11 w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                      {t('verifyEmail.resending')}
                    </>
                  ) : (
                    t('verifyEmail.resend')
                  )}
                </Button>
              </form>

              <p className="mt-6 text-center text-sm">
                <Link
                  to="/login"
                  className="text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('verifyEmail.backToLogin')}
                </Link>
              </p>
            </div>
          )}

          <div className="mt-9 flex items-center justify-between border-t pt-4 font-mono text-xs text-faint">
            <span>{APP_VERSION}</span>
            <span>{t('login.footerMeta')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
